/**
 * Telescope view maths (pure, unit-tested). The panorama is a 16:9 image. The view is a centre point (cx, cy as
 * fractions 0..1 of the panorama) and a zoom. At zoom 1 the round eyepiece shows BASE_FRACTION of the panorama's
 * width, so the whole skyline never fits: you have to pan to look around.
 */
export const BASE_FRACTION = 0.28;
export const MIN_ZOOM = 1;
export const MAX_ZOOM = 2.5;
export const PANO_ASPECT = 16 / 9;
/** The target counts as found when it is this close to the eyepiece centre (fraction of the eyepiece diameter). */
export const FOUND_RADIUS = 0.3;

export interface View {
  cx: number;
  cy: number;
  zoom: number;
}

/** Fraction of the panorama width (w) and height (h) the eyepiece shows at this zoom. */
export function visible(zoom: number): { w: number; h: number } {
  const w = BASE_FRACTION / zoom;
  return { w, h: Math.min(1, w * PANO_ASPECT) };
}

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));

/** Keep the eyepiece inside the panorama and the zoom within limits. */
export function clampView(v: View): View {
  const zoom = clamp(v.zoom, MIN_ZOOM, MAX_ZOOM);
  const f = visible(zoom);
  return { zoom, cx: clamp(v.cx, f.w / 2, 1 - f.w / 2), cy: clamp(v.cy, f.h / 2, 1 - f.h / 2) };
}

/** Pan by a fraction of what is visible (keyboard and buttons: 0.15 = 15% of the eyepiece per press). */
export function panBy(v: View, dxSteps: number, dySteps: number, step = 0.15): View {
  const f = visible(v.zoom);
  return clampView({ ...v, cx: v.cx + dxSteps * f.w * step, cy: v.cy + dySteps * f.h * step });
}

/** Drag by pixels: the image follows the pointer, so the view moves the opposite way. `diameter` = eyepiece px. */
export function dragBy(v: View, dxPx: number, dyPx: number, diameter: number): View {
  const f = visible(v.zoom);
  return clampView({ ...v, cx: v.cx - (dxPx / diameter) * f.w, cy: v.cy - (dyPx / diameter) * f.h });
}

export function zoomBy(v: View, factor: number): View {
  return clampView({ ...v, zoom: v.zoom * factor });
}

/** Distance from the eyepiece centre to a point, as a fraction of the eyepiece diameter. */
export function distanceInEyepiece(v: View, x: number, y: number): number {
  const f = visible(v.zoom);
  return Math.hypot((x - v.cx) / f.w, (y - v.cy) / f.h);
}

/** Is the target (x, y in % of the panorama, r = tolerance in % of the panorama width) centred? */
export function isFound(v: View, t: { x: number; y: number; r: number }): boolean {
  const f = visible(v.zoom);
  const extra = t.r / 100 / f.w;
  return distanceInEyepiece(v, t.x / 100, t.y / 100) <= FOUND_RADIUS + extra;
}

/** Is any of the target visible at all in the round eyepiece? */
export function isVisible(v: View, t: { x: number; y: number; r: number }): boolean {
  const f = visible(v.zoom);
  return distanceInEyepiece(v, t.x / 100, t.y / 100) <= 0.5 + t.r / 100 / f.w;
}

/** Image transform (px) that puts the view centre in the middle of an eyepiece `diameter` px wide. */
export function imageLayout(v: View, diameter: number): { width: number; height: number; left: number; top: number } {
  const f = visible(v.zoom);
  const width = diameter / f.w;
  const height = width / PANO_ASPECT;
  return { width, height, left: diameter / 2 - v.cx * width, top: diameter / 2 - v.cy * height };
}
